import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';

import { getDevices } from '../api/devices';
import { STROKE, SURFACE, TEXT } from '../theme/tokens';

// Serial search across every campaign, in the top bar (FD-462).
//
// This is the search box `TopBar.tsx` refused to ship in FD-274 — "shipping a
// control that does nothing is worse than not shipping it" — and the reason it
// can ship now is that `GET /v1/devices` exists (FD-277). It searches exactly
// what that endpoint searches and says so: a SERIAL PREFIX, nothing else.
//
// The mockups' placeholder reads "Search groups, campaigns, tenants, or
// devices…". It is not reproduced, because three quarters of it would be a
// promise the endpoint cannot keep — there is no server-side search over group
// names, campaign names or tenant domains, and a client-side scan across every
// page of three paginated endpoints is not a search, it is a lie with a spinner.
//
// THE THREE-CHARACTER FLOOR IS THE ENDPOINT'S, not a debounce heuristic:
// `getDevices` refuses a shorter prefix, so the control states the rule rather
// than firing a request that will be rejected.

const MIN_QUERY = 3;

export default function GlobalSearch() {
  const navigate = useNavigate();
  const [raw, setRaw] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(raw.trim()), 250);
    return () => clearTimeout(t);
  }, [raw]);

  // Close on an outside click. The listener is removed on unmount — a dangling
  // document listener is the kind of leak that only shows up after a hundred
  // navigations.
  useEffect(() => {
    function onDocClick(event: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const enabled = debounced.length >= MIN_QUERY;
  const { data, isFetching } = useQuery({
    // The same key shape the Devices page uses, so a search and a visit to that
    // page share one cache entry rather than refetching each other's results.
    queryKey: ['devices', { q: debounced, pageSize: 8 }],
    queryFn: () => getDevices({ q: debounced, pageSize: 8 }),
    enabled,
    staleTime: 30_000,
  });

  const results = data?.devices ?? [];

  return (
    <div ref={boxRef} className="relative w-full max-w-[420px]">
      <div
        className="flex items-center gap-2 rounded-md border px-3"
        style={{ borderColor: STROKE.base, backgroundColor: SURFACE.rowFill, height: 34 }}
      >
        <Search size={15} strokeWidth={1.5} color={TEXT.secondary} aria-hidden="true" />
        <input
          value={raw}
          onChange={(event) => {
            setRaw(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          aria-label="Search devices by serial number"
          placeholder="Search devices by serial…"
          className="w-full border-0 bg-transparent text-[13px] outline-none"
          style={{ color: TEXT.primary }}
        />
      </div>

      {open && raw.trim().length > 0 && (
        <div
          className="absolute left-0 right-0 top-[38px] z-20 overflow-hidden rounded-md border"
          style={{ borderColor: STROKE.base, backgroundColor: SURFACE.base }}
        >
          {!enabled ? (
            <div className="px-3 py-2.5 text-[12px]" style={{ color: TEXT.secondary }}>
              Type at least {MIN_QUERY} characters of a serial number.
            </div>
          ) : isFetching ? (
            <div className="px-3 py-2.5 text-[12px]" style={{ color: TEXT.secondary }}>
              Searching…
            </div>
          ) : results.length === 0 ? (
            <div className="px-3 py-2.5 text-[12px]" style={{ color: TEXT.secondary }}>
              No device serial starts with “{debounced}”.
            </div>
          ) : (
            results.map((device) => (
              <button
                key={device.serialNumber}
                type="button"
                onClick={() => {
                  setOpen(false);
                  setRaw('');
                  navigate(`/devices/${encodeURIComponent(device.serialNumber)}`);
                }}
                className="flex w-full cursor-pointer items-baseline justify-between gap-3 border-0 bg-transparent px-3 py-2 text-left hover:bg-black/[0.03]"
              >
                <span
                  className="text-[13px] font-medium"
                  style={{ color: TEXT.primary, fontVariantNumeric: 'tabular-nums' }}
                >
                  {device.serialNumber}
                </span>
                <span className="truncate text-[11px]" style={{ color: TEXT.secondary }}>
                  {device.migrationId}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
